import { useEffect, useRef, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { useSync } from "@/context/SyncContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { captureFrameToFile, stopStream } from "@/lib/captureGuide";
import {
  X, Camera, Check, Loader2, RotateCw, Layers, ImageOff, AlertTriangle, ArrowLeft, Link2,
} from "lucide-react";

// Feature 16 — Same-View Capture Guide (FREE). Move-out capture only.
// Steps: pick reference -> start camera -> align with translucent overlay -> capture -> review
// (no overlay) -> use -> upload via provided pipeline -> explicit pairing.
export function SameViewCaptureGuide({ propertyId, roomId, roomName, onClose, onUpload }) {
  const { createPairingPending, confirmPairing } = useSync();
  const [step, setStep] = useState("select"); // select | camera | review | confirm | done
  const [baselines, setBaselines] = useState(null);
  const [selected, setSelected] = useState(null);
  const [camState, setCamState] = useState("idle"); // idle | starting | live | denied | unsupported | error | inuse
  const [opacity, setOpacity] = useState(45);
  const [overlayOn, setOverlayOn] = useState(true);
  const [captured, setCaptured] = useState(null); // {file, previewUrl}
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pairState, setPairState] = useState("idle"); // idle | paired | failed
  const [camRes, setCamRes] = useState(null); // {w,h} actual live track resolution
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => { stopStream(streamRef.current); streamRef.current = null; }, []);

  useEffect(() => {
    api.get(`/properties/${propertyId}/same-view/baselines`, { params: { room_id: roomId } })
      .then((r) => setBaselines(r.data.baselines || []))
      .catch((e) => { setBaselines([]); if (e?.response?.status !== 400) toast.error("Couldn't load references."); });
  }, [propertyId, roomId]);

  // Clean up camera on unmount / close.
  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { setCamState("unsupported"); return; }
    setCamState("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        // record the ACTUAL live resolution (track settings + intrinsic video size)
        const s = stream.getVideoTracks?.()[0]?.getSettings?.() || {};
        const w = s.width || videoRef.current.videoWidth || 0;
        const h = s.height || videoRef.current.videoHeight || 0;
        setCamRes({ w, h });
      }
      setCamState("live");
    } catch (e) {
      stopCamera();
      const n = e?.name || "";
      if (n === "NotAllowedError" || n === "SecurityError") setCamState("denied");
      else if (n === "NotReadableError") setCamState("inuse");
      else if (n === "NotFoundError") setCamState("error");
      else setCamState("error");
    }
  };

  const openCamera = (b) => { setSelected(b); setStep("camera"); setCamState("idle"); };

  const doCapture = async () => {
    try {
      const shot = await captureFrameToFile(videoRef.current);
      setCaptured(shot);
      stopCamera(); // stop the stream once we have the frame
      setCamState("idle");
      setStep("review");
    } catch { toast.error("Couldn't capture — try again."); }
  };

  const retake = () => {
    if (captured?.previewUrl) URL.revokeObjectURL(captured.previewUrl);
    setCaptured(null);
    setStep("camera"); setCamState("idle");
  };

  const usePhoto = async () => {
    if (!captured) return;
    setBusy(true);
    try {
      const photo = await onUpload(captured.file); // resilient queue: exact bytes persisted, then upload
      if (!photo?.photo_id) { toast.error("Upload didn't complete. Your photo was not paired."); setBusy(false); return; }
      // Persist ONLY minimal pairing metadata (survives reload). Raw Blob already removed by the
      // queue on successful upload. Pairing is NOT called automatically — the user must confirm.
      setUploadedPhoto(photo);
      await createPairingPending({
        move_out_photo_id: photo.photo_id,
        baseline_photo_id: selected.baseline_photo_id,
        property_id: propertyId,
        room_id: roomId,
        room_label: roomName,
      });
      setStep("confirm");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  const confirmNow = async () => {
    if (!uploadedPhoto) return;
    setBusy(true);
    const res = await confirmPairing({
      id: uploadedPhoto.photo_id, move_out_photo_id: uploadedPhoto.photo_id,
      baseline_photo_id: selected.baseline_photo_id, property_id: propertyId,
    });
    setBusy(false);
    if (res.ok) { setPairState("paired"); }
    else { setPairState("failed"); if (res.needsAttention) toast.error("That baseline is no longer available."); }
    setStep("done");
  };

  const close = () => { stopCamera(); if (captured?.previewUrl) URL.revokeObjectURL(captured.previewUrl); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] bg-[#0F1B2D] text-white flex flex-col" data-testid="same-view-guide">
      <header className="flex items-center justify-between px-4 h-14 border-b border-white/10" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button data-testid="sv-close" onClick={close} className="grid place-items-center h-11 w-11 rounded-full bg-white/10 active:bg-white/25"><X size={20} /></button>
        <span className="font-medium truncate flex items-center gap-1.5"><Layers size={16} className="text-amber" /> Same-View Guide · {roomName}</span>
        <span className="w-11" />
      </header>

      {/* STEP: select reference */}
      {step === "select" && (
        <div className="flex-1 overflow-y-auto px-4 py-5" data-testid="sv-select">
          <p className="text-sm text-white/70">Pick a move-in reference photo for this room. Use it as a visual guide while framing the new shot — TenantSeal does not verify that the angle is an exact match.</p>
          {baselines === null ? (
            <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-amber" /></div>
          ) : baselines.length === 0 ? (
            <div className="mt-8 text-center" data-testid="sv-no-baseline">
              <ImageOff size={30} className="mx-auto text-white/40" />
              <p className="mt-3 text-sm text-white/70">No eligible move-in reference is available for this room.</p>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {baselines.map((b) => (
                <button key={b.baseline_photo_id} data-testid={`sv-ref-${b.baseline_photo_id}`} onClick={() => openCamera(b)}
                  className="relative aspect-square rounded-2xl overflow-hidden border border-white/15 active:scale-95 transition-transform">
                  <img src={fileUrl(b.storage_path, 400)} alt="move-in reference" loading="lazy" decoding="async"
                    width="360" height="360" className="w-full h-full object-cover" />
                  <span className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold bg-black/55 rounded px-1.5 py-0.5">Move-in reference</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP: camera + overlay */}
      {step === "camera" && (
        <div className="flex-1 relative overflow-hidden" data-testid="sv-camera">
          {/* live camera preview (rear, not mirrored) */}
          <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover bg-black" data-testid="sv-video" />
          {/* reference overlay — separate DOM layer, never composited into the capture */}
          {camState === "live" && overlayOn && selected && (
            <img src={fileUrl(selected.storage_path, 1280)} alt="" aria-hidden="true" width="390" height="640" decoding="async"
              data-testid="sv-overlay" className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ opacity: opacity / 100 }} />
          )}

          {camState !== "live" && (
            <div className="absolute inset-0 grid place-items-center px-6 text-center">
              {camState === "idle" && (
                <div>
                  <Camera size={34} className="mx-auto text-white/60" />
                  <p className="mt-3 text-sm text-white/70 max-w-xs">Start the camera to see your move-in photo as a translucent guide.</p>
                  <Button data-testid="sv-start-camera" onClick={startCamera} className="mt-4 h-12 rounded-xl bg-amber text-navy font-semibold px-6"><Camera size={16} className="mr-1.5" /> Start camera</Button>
                </div>
              )}
              {camState === "starting" && <Loader2 className="animate-spin text-amber" />}
              {(camState === "denied" || camState === "unsupported" || camState === "error" || camState === "inuse") && (
                <div data-testid="sv-camera-fallback">
                  <AlertTriangle size={30} className="mx-auto text-amber" />
                  <p className="mt-3 text-sm text-white/80 max-w-xs">
                    {camState === "denied" && "Camera permission was denied. You can still capture with the regular camera."}
                    {camState === "unsupported" && "Your browser doesn't support in-app camera preview. Use the regular camera instead."}
                    {camState === "inuse" && "Your camera is in use by another app. Close it and retry, or use the regular camera."}
                    {camState === "error" && "Couldn't start the camera. You can still capture with the regular camera."}
                  </p>
                  <div className="mt-4 flex gap-2 justify-center">
                    {(camState === "denied" || camState === "error" || camState === "inuse") && (
                      <Button data-testid="sv-retry-camera" onClick={startCamera} variant="outline" className="h-11 rounded-xl border-white/20 text-white">Retry</Button>
                    )}
                    <Button data-testid="sv-fallback-close" onClick={close} className="h-11 rounded-xl bg-white/15 text-white">Use regular camera</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* controls */}
          {camState === "live" && (
            <>
              <div className="absolute top-3 left-3 right-3 flex items-center gap-2" data-testid="sv-controls-top">
                <button data-testid="sv-overlay-toggle" onClick={() => setOverlayOn((v) => !v)}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 ${overlayOn ? "bg-amber text-navy" : "bg-white/15 text-white"}`}>
                  <Layers size={13} /> {overlayOn ? "Overlay on" : "Overlay off"}
                </button>
                <button data-testid="sv-opacity-reset" onClick={() => setOpacity(45)} className="text-xs font-semibold rounded-full px-3 py-1.5 bg-white/15">Reset</button>
              </div>
              {camRes && Math.max(camRes.w, camRes.h) > 0 && Math.max(camRes.w, camRes.h) < 1280 && (
                <div data-testid="sv-lowres-note" className="absolute top-16 left-3 right-3 text-[11px] text-navy bg-amber rounded-lg px-3 py-2">
                  This guided preview is {camRes.w}×{camRes.h}, lower than your device's regular camera. For full-resolution evidence, use the regular camera — it's not the same resolution as guided capture.
                  <button data-testid="sv-lowres-regular" onClick={close} className="ml-1 underline font-semibold">Use regular camera</button>
                </div>
              )}
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/70 to-transparent" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }} data-testid="sv-controls-bottom">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[11px] text-white/70 w-14">Opacity</span>
                  <input data-testid="sv-opacity-slider" type="range" min="10" max="80" value={opacity}
                    onChange={(e) => setOpacity(Number(e.target.value))} className="flex-1 accent-amber" />
                  <span className="text-[11px] text-white/70 w-8 text-right">{opacity}%</span>
                </div>
                <div className="flex items-center justify-center">
                  <button data-testid="sv-capture" onClick={doCapture}
                    className="grid place-items-center h-[70px] w-[70px] rounded-full bg-white ring-4 ring-white/25 active:scale-95 transition-transform">
                    <span className="grid place-items-center h-14 w-14 rounded-full bg-brand"><Camera size={26} /></span>
                  </button>
                </div>
                <p className="mt-2 text-center text-[11px] text-white/60">Visual guide only — align the room, then capture when ready.</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* STEP: review (no overlay) */}
      {step === "review" && captured && (
        <div className="flex-1 flex flex-col px-4 py-5" data-testid="sv-review">
          <p className="text-sm text-white/70">Review your new photo. The move-in guide is not part of this image.</p>
          <div className="mt-3 flex-1 grid place-items-center">
            <img src={captured.previewUrl} alt="captured" data-testid="sv-review-img" className="max-h-[55vh] rounded-xl border border-white/10 object-contain" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <Button data-testid="sv-retake" onClick={retake} disabled={busy} className="h-12 rounded-xl bg-white/10 text-white"><RotateCw size={16} className="mr-1.5" /> Retake</Button>
            <Button data-testid="sv-use" onClick={usePhoto} disabled={busy} className="h-12 rounded-xl bg-amber text-navy font-semibold">
              {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Check size={16} className="mr-1.5" />} Use photo
            </Button>
          </div>
        </div>
      )}

      {/* STEP: confirm pairing (explicit — never automatic) */}
      {step === "confirm" && (
        <div className="flex-1 grid place-items-center px-6 text-center" data-testid="sv-confirm">
          <div>
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-indigo-500/20 text-indigo-300 mx-auto"><Link2 size={26} /></span>
            <p className="mt-3 font-semibold" data-testid="sv-confirm-msg">Photo uploaded — confirm pairing?</p>
            <p className="mt-1 text-sm text-white/60">Match this move-out photo to the move-in reference for {roomName}. You can also do this later.</p>
            <div className="grid grid-cols-2 gap-3 mt-5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <Button data-testid="sv-pair-later" onClick={close} disabled={busy} className="h-12 rounded-xl bg-white/10 text-white">Later</Button>
              <Button data-testid="sv-pair-now" onClick={confirmNow} disabled={busy} className="h-12 rounded-xl bg-amber text-navy font-semibold">
                {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Link2 size={16} className="mr-1.5" />} Pair now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* STEP: done */}
      {step === "done" && (
        <div className="flex-1 grid place-items-center px-6 text-center" data-testid="sv-done">
          <div>
            {pairState === "paired" ? (
              <>
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-emerald-500/20 text-emerald-300 mx-auto"><Link2 size={26} /></span>
                <p className="mt-3 font-semibold" data-testid="sv-paired-msg">Photo paired for Proof Replay</p>
                <p className="mt-1 text-sm text-white/60">Your move-out photo is saved and linked to the move-in reference.</p>
              </>
            ) : (
              <>
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-amber-400/20 text-amber-300 mx-auto"><AlertTriangle size={26} /></span>
                <p className="mt-3 font-semibold" data-testid="sv-pair-failed-msg">Photo saved — pairing didn't complete</p>
                <p className="mt-1 text-sm text-white/60">Your photo is safe. You can pair it later from Proof Replay.</p>
              </>
            )}
            <Button data-testid="sv-done-close" onClick={close} className="mt-5 h-12 rounded-xl bg-white/15 text-white px-6"><ArrowLeft size={16} className="mr-1.5" /> Back to capture</Button>
          </div>
        </div>
      )}
    </div>
  );
}
